import { useEffect, useMemo, useState } from 'react'
import { Handbuchwert, handbuchwertart } from './Handbuchwertart'
import { Zahlenfeld } from './Zahlenfeld'
import { Betrag } from './Betrag'
import { Abrufausfall, useHintergrundabruf } from './Abruf'
import type { Abruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Auswahlfeld } from './Auswahlfeld'
import type { Auswahleintrag } from './Auswahlfeld'
import { Dialogfenster } from './Dialogfenster'
import { Gliederungswahl } from './Gliederungswahl'
import { Kontofeld } from './Kontofeld'
import { Icabstimmung } from './Icabstimmung'
import { useDarf } from './Rechte'
import { GRENZT_AB, MIT_DIFFERENZKONTO, MIT_ERTRAGSPOSITIONEN, MIT_KAPITALPOSITIONEN, MIT_MARGE, MIT_WAEHRUNGSKONTO } from './Schrittfolge'
import {
  aktuelleVorzeichenart,
  anteil,
  anzahlwort,
  gesellschaftsname,
  katalogbezeichnung,
  kontobezeichnung,
  periodenkuerzel,
  periodenname,
  regelname,
  satz,
  sollHaben,
  t,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type {
  Belegfall,
  Buchungszeile,
  Korrekturangabe,
  Prozessgrundlage,
  Prozessnachweis,
  Regelparameter,
  Schrittzeile,
  Stammdatenkatalog,
  Werkzeug,
} from './typen'

/**
 * Ein Konsolidierungsprozeß, geöffnet aus seinem Konzernabschluß
 * (2.13 Abschnitt 10, R814–R817).
 *
 * **Auftrag vom 2026-09-27:** *„Wenn ich den KA aufklappe und die AEKO
 * anklicke, soll ich sie bearbeiten können. Dies soll durch ein Modal
 * erfolgen, damit der Bildschirm sauber bleibt und nicht überladen wird."*
 *
 * **Zwei Blätter, nicht zwei Fenster** (R814): *Einstellungen* sagt, woraus
 * der Prozeß liest und wohin er bucht; *Erzeugte Belege* zeigt, was dabei
 * herauskam. Beide gelten für jeden Prozeß — auch für die neun, die keine
 * Abgrenzung kennen.
 */
export function Prozessfenster({
  schritt,
  werkzeug,
  konzernabschluss,
  parameter,
  ausgabe,
  handbuchabruf,
  kontowahl,
  positionswahl,
  bezug,
  aufGespeichert,
  aufSchliessen,
}: {
  schritt: Schrittzeile
  /** Der Vorrat sagt, was dieser Prozeß liest und bucht (R816). */
  werkzeug: Werkzeug | undefined
  konzernabschluss: string
  /** Die Werte der geltenden Handbuchausgabe — `null`: es gibt keine. */
  parameter: Regelparameter | null
  /** Welche Ausgabe das ist — für den Satz, aus dem die Werte stammen. */
  ausgabe: number | null
  /**
   * Wie das Konzernhandbuch geholt wurde (7.2 Abschnitt 39, R1210): Kam es
   * nicht, gilt nicht „keine Ausgabe", sondern „ließ sich nicht laden".
   */
  handbuchabruf?: Pick<Abruf<unknown>, 'ausgefallen' | 'erneut'>
  kontowahl: Auswahleintrag[]
  positionswahl: Auswahleintrag[]
  /**
   * Woraus der Gliederungsbaum geholt wird (R826) — Bewertungsebene und
   * Periode des Konzernabschlusses, zu dem dieser Prozeß gehört.
   *
   * <c>null</c> heißt: Es gibt keinen Bezug, aus dem sich eine Gliederung
   * holen ließe. Dann steht statt der Knöpfe der Grund da; ein Knopf, der ein
   * leeres Fenster öffnet, wäre die schlechtere Auskunft.
   */
  bezug: { ledger: string; periode: number } | null
  aufGespeichert: (folge: Schrittzeile[]) => void
  aufSchliessen: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse1, rahmen: spaltenrahmen1 } =
    useSpaltenmasse<HTMLTableElement>('prozessfenster:prozessfenster:1')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse2, rahmen: spaltenrahmen2 } =
    useSpaltenmasse<HTMLTableElement>('prozessfenster:prozessfenster:2')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse3, rahmen: spaltenrahmen3 } =
    useSpaltenmasse<HTMLTableElement>('prozessfenster:prozessfenster:3')

  /*
    **Was der Angemeldete nicht darf, ist nicht da** (8.1 Abschnitt 7, R1180):
    Wer nur lesen darf, liest die Einstellungen als Text — ohne Feld, ohne
    Knopf und ohne Speichern.
  */
  const darf = useDarf()
  const darfUebersteuern = darf('konsolidierungsschrittUebersteuern')
  const darfKorrigieren = darf('konsolidierungsschrittKorrigieren')
  const darfAbgrenzen = darf('konsolidierungsschrittAbgrenzen')

  const [blatt, setBlatt] = useState<'einstellungen' | 'belege'>('einstellungen')
  const [nachweis, setNachweis] = useState<Prozessnachweis | null>(null)
  const [monat, setMonat] = useState<number | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  /**
   * Die Abweichung, wie der Bildschirm sie hält (R817).
   *
   * **Der Schalter ist eine eigene Angabe und nicht abgeleitet.** Wer ihn
   * anschaltet und noch nichts eingetragen hat, ist im Zustand *„ich weiche
   * gleich ab"* — abgeleitet aus den Feldern gäbe es diesen Zustand nicht,
   * und die Felder blieben gesperrt, sobald man sie leert.
   */
  const [weichtAb, setWeichtAb] = useState(schritt.uebersteuerungJson !== null)
  const [abweichung, setAbweichung] = useState<Record<string, string>>(() =>
    liesAbweichung(schritt.uebersteuerungJson),
  )

  /**
   * Was dieser Prozeß umgliedert und saldiert (R818).
   *
   * **Nur ein Werkzeug liest sie** — an jedem anderen Prozeß wäre die Liste
   * eine Zusage, die der Rechenkern nicht hält.
   */
  const [korrekturen, setKorrekturen] = useState<Korrekturangabe[]>(() =>
    liesKorrekturen(schritt.ausweiskorrekturJson),
  )

  // **Umgliedern und Saldieren sind zwei Schritte** (2.9 Abschnitt 15.9,
  // R1615) — dieselbe Liste am Prozess, je Schritt ihre eigene Zeile.
  const istSaldierung = schritt.werkzeug === 'saldierung'
  const istKorrekturprozess = schritt.werkzeug === 'ausweiskorrektur' || istSaldierung
  const istPosition = (wert: string) => positionswahl.some((e) => e.wert === wert)
  const positionsname = (wert: string) => positionswahl.find((e) => e.wert === wert)?.text ?? wert
  // Die Quelle einer Ausweiskorrektur ist eine Position oder ein Konto (R1616).
  const quellwahl = useMemo(() => [...positionswahl, ...kontowahl], [positionswahl, kontowahl])

  /**
   * Die IC-Abstimmung steht **im Konzernabschluß und nicht im Menü**
   * (Auftrag vom 2026-09-27: *„er sollte eigentlich direkt in den KA rein"*).
   *
   * **Sie ist einer der Prozesse dieses Abschlusses**, und alles, was zu
   * einem Prozeß gehört, öffnet sein Fenster (R814). Ein eigener Menüpunkt
   * daneben wäre ein zweiter Weg zu derselben Sache — und einer, der den
   * Konzernabschluß noch einmal erfragt, den man gerade offen hat.
   */
  const istAbstimmung = schritt.werkzeug === 'ic-abstimmung'

  /**
   * Woran dieser Prozeß arbeitet (R800, R825).
   *
   * **Sie steht hier und nicht mehr im Vorlagenbereich.** Bis zum 2026-09-27
   * stand an dieser Stelle der Satz „gepflegt wird die Abgrenzung im
   * Vorlagenbereich" — eine Sackgasse, und obendrein die falsche Ebene: Die
   * Abgrenzung gehört nach R789 dem Konzernabschluß und darf von Jahr zu Jahr
   * abweichen.
   *
   * **Toleranz als Text und nicht als Zahl**: Ein leeres Feld heißt „die
   * Grenze aus dem Regelwerk" und ist nicht dasselbe wie die Null.
   */
  const [abgrenzung, setAbgrenzung] = useState<Abgrenzungsentwurf>(() => liesAbgrenzung(schritt.abgrenzungJson))

  /** Welches Auswahlfenster offensteht — eines zur Zeit (R610). */
  const [waehlt, setWaehlt] = useState<Positionsfeld | null>(null)

  const grenztAb = GRENZT_AB.has(schritt.werkzeug)
  const fuehrtKonto = MIT_DIFFERENZKONTO.has(schritt.werkzeug)
  const trenntWaehrung = MIT_WAEHRUNGSKONTO.has(schritt.werkzeug)
  const mitMarge = MIT_MARGE.has(schritt.werkzeug)
  // **Kapital- und Dividendenkonsolidierung wählen ihre Positionen** (7.4
  // Abschnitt 34.12, R1603).
  const mitKapitalpositionen = MIT_KAPITALPOSITIONEN.has(schritt.werkzeug)
  const mitErtragspositionen = MIT_ERTRAGSPOSITIONEN.has(schritt.werkzeug)
  const waehltPositionen = grenztAb || mitMarge || mitErtragspositionen
  const grenztEin = fuehrtKonto || mitMarge || mitKapitalpositionen

  /*
    **Die Gesellschaften für die Margen je Lieferbeziehung** (2.9 Abschnitt
    13.16, R1561) — geholt nur, wo es Margen gibt.
  */
  const katalog = useHintergrundabruf<Stammdatenkatalog | null>(
    () => (mitMarge ? api.stammdatenkatalog() : Promise.resolve(null)),
    [mitMarge],
    null,
  )
  const gesellschaftswahl = useMemo(
    () =>
      (katalog?.einheiten ?? []).map((e) => ({
        wert: e.schluessel,
        text: katalogbezeichnung(e),
        stillgelegtAb: e.stillgelegtAb,
      })),
    [katalog],
  )

  function aenderePaar(stelle: number, was: Partial<Paarmargenentwurf>) {
    setAbgrenzung((a) => ({
      ...a,
      paarmargen: a.paarmargen.map((p, i) => (i === stelle ? { ...p, ...was } : p)),
    }))
  }

  function aendere(stelle: number, was: Partial<Korrekturangabe>) {
    setKorrekturen((bisher) =>
      bisher.map((regel, i) => (i === stelle ? { ...regel, ...was } : regel)),
    )
  }

  /**
   * Woran dieser Prozeß rechnet (R822) — bei der Kapitalkonsolidierung ihre
   * Beteiligungen.
   */
  const [grundlage, setGrundlage] = useState<Prozessgrundlage | null>(null)
  const [suche, setSuche] = useState('')
  const [alleZeigen, setAlleZeigen] = useState(false)

  /*
    **Geholt wird einmal beim Öffnen**, und nur für die Werkzeuge, die eine
    Grundlage führen: Ein Ruf an einem Prozeß, der keine hat, käme leer zurück
    und hätte doch gefragt.
  */
  useEffect(() => {
    if (schritt.werkzeug !== 'kapitalkonsolidierung') {
      return
    }

    let gilt = true

    api
      .konsolidierungsschrittGrundlage(schritt.schluessel)
      .then((geholt) => {
        if (gilt) {
          setGrundlage(geholt)
        }
      })
      .catch(() => {})

    return () => {
      gilt = false
    }
  }, [schritt.schluessel, schritt.werkzeug])

  /**
   * Was in der Liste steht.
   *
   * **Zuerst, was eine Frage aufwirft** (R822): Wer sucht, sucht alles; wer
   * nicht sucht, sieht die Auffälligen — und den Rest auf Klick.
   */
  const gefunden = useMemo(() => {
    const alle = grundlage?.beteiligungen ?? []
    const gesucht = suche.trim().toLowerCase()

    if (gesucht === '') {
      return alle
    }

    return alle.filter(
      (b) =>
        gesellschaftsname(b.tochter).toLowerCase().includes(gesucht)
        || gesellschaftsname(b.mutter).toLowerCase().includes(gesucht),
    )
  }, [grundlage, suche])

  /**
   * **Kurze Listen stehen ganz da.**
   *
   * Die Ausblendung ist für achtzig Gesellschaften gemacht; bei einer
   * einzigen Beteiligung zeigte sie nichts und böte an, „die übrige eine" zu
   * zeigen. Die Schwelle ist dieselbe wie beim Auswahlfeld (R583): Ab
   * dreizehn Einträgen lohnt das Verstecken.
   */
  /** Wonach die Belege gesucht und geordnet werden (Auftrag vom 2026-09-27). */
  const [fallsuche, setFallsuche] = useState('')
  const [fallordnung, setFallordnung] = useState<'gesellschaft' | 'regel' | 'betrag'>('gesellschaft')

  /**
   * Die Fälle, wie sie dastehen.
   *
   * **Gesucht wird nach Gesellschaft**, weil danach jemand sucht: Ein Monat
   * mit achtzig Gesellschaften trägt ebenso viele Fälle, und wer einen davon
   * prüft, kennt seinen Namen.
   *
   * **Geordnet wird nach dem Betrag absteigend**, wo der Betrag gewählt ist —
   * wer nach Größe ordnet, sucht den größten. Gemessen wird die Sollseite:
   * Ein Fall geht auf null auf, und seine Summe wäre als Maß immer dieselbe.
   */
  const faelle = useMemo(() => {
    const alle = nachweis?.faelle ?? []
    const gesucht = fallsuche.trim().toLowerCase()

    const gefiltert =
      gesucht === ''
        ? alle
        : alle.filter(
            (f) =>
              f.gesellschaften.some((g) => gesellschaftsname(g).toLowerCase().includes(gesucht))
              || regelname(f.regel).toLowerCase().includes(gesucht),
          )

    // Die Spiegelung des Ergebnisses zählt nicht mit (R1639).
    const soll = (f: Belegfall) =>
      f.zeilen.filter((z) => z.betrag > 0 && !istAusweis(z)).reduce((summe, z) => summe + z.betrag, 0)

    return [...gefiltert].sort((x, y) => {
      if (fallordnung === 'betrag') {
        return soll(y) - soll(x)
      }

      if (fallordnung === 'regel') {
        return regelname(x.regel).localeCompare(regelname(y.regel))
      }

      return namenswahl(x).localeCompare(namenswahl(y))
    })
  }, [nachweis, fallsuche, fallordnung])

  /** Wonach die Beteiligungen sortiert sind — der Kopf schaltet um. */
  const [sortierung, setSortierung] = useState<'tochter' | 'quote' | 'erwerb'>('tochter')
  const [absteigend, setAbsteigend] = useState(false)

  function sortiereNach(nach: 'tochter' | 'quote' | 'erwerb') {
    if (nach === sortierung) {
      setAbsteigend((bisher) => !bisher)
      return
    }

    setSortierung(nach)
    setAbsteigend(false)
  }

  const kurz = (grundlage?.gesamt ?? 0) <= 12

  const sichtbare = useMemo(() => {
    const auswahl =
      suche.trim() !== '' || alleZeigen || kurz
        ? gefunden
        : gefunden.filter((b) => b.auffaellig)

    /*
      **Sortiert wird über die ganze Liste, nicht über die Seite** (Auftrag
      vom 2026-09-27): Wer bei achtzig Gesellschaften nach der Quote sortiert,
      sucht die größte -- und die stünde sonst auf einer Seite, die er nicht
      sieht.
    */
    const geordnet = [...auswahl].sort((x, y) => {
      if (sortierung === 'quote') {
        return x.quote - y.quote
      }

      if (sortierung === 'erwerb') {
        return (x.erwerbsperiode ?? 0) - (y.erwerbsperiode ?? 0)
      }

      return gesellschaftsname(x.tochter).localeCompare(gesellschaftsname(y.tochter))
    })

    return absteigend ? geordnet.reverse() : geordnet
  }, [gefunden, suche, alleZeigen, kurz, sortierung, absteigend])

  const versteckt = kurz
    ? 0
    : gefunden.length - gefunden.filter((b) => b.auffaellig).length

  const handbuchwerte = werkzeug?.handbuchwerte ?? []
  const rechnetNiemand = (werkzeug?.vorgaenge.length ?? 0) === 0

  /*
    **Das Belegblatt lädt erst, wenn es aufgeschlagen wird.** Ein Fenster, das
    beim Öffnen zwei Monate Buchungen holt, die niemand sehen will, wäre bei
    achtzig Gesellschaften die teuerste Art, nichts zu zeigen.
  */
  useEffect(() => {
    if (blatt !== 'belege' || rechnetNiemand) {
      return
    }

    let gilt = true
    setLaeuft(true)

    api
      .konsolidierungsschrittNachweis(schritt.schluessel, monat)
      .then((geholt) => {
        if (gilt) {
          setNachweis(geholt)
          setMonat(geholt.periode)
        }
      })
      .catch((f) => {
        // Nur ein fachlicher Satz steht hier; eine Störung meldet das
        // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
        if (gilt) {
          setMeldung(fachlicherSatz(f))
        }
      })
      .finally(() => {
        if (gilt) {
          setLaeuft(false)
        }
      })

    return () => {
      gilt = false
    }
  }, [blatt, monat, rechnetNiemand, schritt.schluessel])

  const werkzeugname = schritt.werkzeug
    ? t(`werkzeug.${schritt.werkzeug}` as Sprachschluessel)
    : ''

  // Gespeichert wird nur, was der Angemeldete ändern darf (R1180).
  const kannSpeichern =
    (handbuchwerte.length > 0 && darfUebersteuern)
    || (istKorrekturprozess && darfKorrigieren)
    || (grenztEin && darfAbgrenzen)

  async function speichere() {
    setLaeuft(true)
    setMeldung(null)

    try {
      // Ausgeschalteter Schalter heißt: zurückgenommen. Der Server
      // unterscheidet nicht zwischen „leer" und „nicht gesetzt" (R817).
      // Gespeichert wird, was dieser Prozeß trägt: die Abweichung vom
      // Konzernhandbuch (R817) und, wo es sie gibt, die Ausweiskorrekturen
      // (R818). Ein Ruf für etwas, das das Werkzeug nicht liest, würde
      // abgewiesen -- zu Recht.
      let folge = handbuchwerte.length > 0 && darfUebersteuern
        ? await api.konsolidierungsschrittUebersteuern(
            schritt.schluessel,
            weichtAb ? abweichung : {},
            konzernabschluss,
          )
        : []

      if (istKorrekturprozess && darfKorrigieren) {
        folge = await api.konsolidierungsschrittKorrigieren(
          schritt.schluessel,
          korrekturen.filter((r) => r.quelle !== '' && r.ziel !== ''),
          konzernabschluss,
        )
      }

      // **Die Abgrenzung wird am Prozeß gespeichert** (R825), zusammen mit
      // allem anderen: Zwei Speichern-Knöpfe in einem Blatt wären zwei
      // Zustände, von denen einer ungesichert ist.
      if (grenztEin && darfAbgrenzen) {
        const toleranz = abgrenzung.toleranz.trim()
        folge = await api.konsolidierungsschrittAbgrenzen(
          schritt.schluessel,
          {
            positionen: waehltPositionen ? abgrenzung.positionen : [],
            beteiligungen: mitKapitalpositionen ? abgrenzung.beteiligungen : null,
            eigenkapital: mitKapitalpositionen ? abgrenzung.eigenkapital : null,
            differenzkonto: fuehrtKonto ? abgrenzung.differenzkonto.trim() || null : null,
            toleranz: !fuehrtKonto || toleranz === '' ? null : Number(toleranz.replace(',', '.')),
            differenzkontoWaehrung: trenntWaehrung ? abgrenzung.differenzkontoWaehrung.trim() || null : null,
            // **Die Marge wird in Prozent eingegeben und als Anteil
            // gespeichert** (R1562): 20 % am Bildschirm, 0.2 in der Abgrenzung.
            standardmarge: mitMarge ? alsAnteil(abgrenzung.standardmarge) : null,
            paarmargen: mitMarge
              ? abgrenzung.paarmargen
                  .filter((p) => p.lieferant !== '' && p.empfaenger !== '' && p.marge.trim() !== '')
                  .map((p) => ({ lieferant: p.lieferant, empfaenger: p.empfaenger, marge: alsAnteil(p.marge) ?? 0 }))
              : null,
          },
          konzernabschluss || null,
        )
      }

      aufGespeichert(folge)
      // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
      meldeErfolg(t('erfolg.prozess.gespeichert'))
      setMeldung(null)
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={schritt.namen.de || schritt.schluessel}
      kopfzusatz={werkzeugname}
      weite="breit"
      aufSchliessen={aufSchliessen}
      fuss={
        // Ein Speichern an einem Prozess ohne eine einzige einstellbare Angabe
        // waere ein Knopf, der nichts tut (R803).
        blatt === 'einstellungen' && (handbuchwerte.length > 0 || istKorrekturprozess || grenztEin) && kannSpeichern ? (
          <>
            <button type="button" className="haupt" disabled={laeuft} onClick={() => void speichere()}>
              {t('prozess.speichern')}
            </button>
            <button type="button" onClick={aufSchliessen}>
              {t('prozess.schliessen')}
            </button>
            {meldung && <span className="erlaeuterung">{meldung}</span>}
          </>
        ) : (
          <button type="button" onClick={aufSchliessen}>
            {t('prozess.schliessen')}
          </button>
        )
      }
    >
      <div className="prozessfenster">
        {/*
          **Zwei Blätter und kein zweites Fenster** (R814). Die Zahl am Reiter
          sagt, ob sich das Aufschlagen lohnt — ein Reiter ohne Zahl ist eine
          Tür, hinter der ebensogut nichts sein kann.
        */}
        <div className="reiterleiste">
          <button
            type="button"
            className={blatt === 'einstellungen' ? 'reiter aktiv' : 'reiter'}
            aria-pressed={blatt === 'einstellungen'}
            onClick={() => setBlatt('einstellungen')}
          >
            {t('prozess.blatt.einstellungen')}
          </button>
          <button
            type="button"
            className={blatt === 'belege' ? 'reiter aktiv' : 'reiter'}
            aria-pressed={blatt === 'belege'}
            onClick={() => setBlatt('belege')}
          >
            {t('prozess.blatt.belege')}
            {/* Eine Null am Reiter ist keine Auskunft, sondern Rauschen. */}
            {nachweis !== null && nachweis.faelle.length > 0 && (
              <span className="zahl">{anzahlwort(nachweis.faelle.length)}</span>
            )}
          </button>
        </div>

        {blatt === 'einstellungen' && (
          <div className="einstellungsblatt">
            {/*
              **Woraus gelesen wird, steht als gepflegter Satz** (R816): Der
              Rechenkern hält es fest, gepflegt wird es hier. Ein Werkzeug ohne
              diesen Satz meldet der Prüffall.
            */}
            <p className="liest">{t(`werkzeug.${schritt.werkzeug}.liest` as Sprachschluessel)}</p>

            {/*
              **Das Abstimmblatt steht im Fenster seines Prozesses**
              (2.10 Abschnitt 9, R827): zwei Blätter, die ganze Zeitreihe des
              Konzernabschlusses, und ein Klick klappt ein Pärchen auf.
            */}
            {istAbstimmung && konzernabschluss !== '' && (
              <Icabstimmung abschluss={konzernabschluss} />
            )}

            {/*
              **Nicht bei der Abstimmung** (R827): Daß sie nichts bucht, ist
              kein Mangel, sondern ihr Wesen — und ihr eigenes Blatt sagt es
              eine Zeile weiter oben in den richtigen Worten. Zweimal
              dasselbe, einmal davon rot, macht aus einer Tatsache einen
              Befund.
            */}
            {rechnetNiemand && !istAbstimmung && (
              <p className="meldung hinweis">{t('prozess.ohnevorgang')}</p>
            )}

            {grenztEin && (
              <>
                {(waehltPositionen || mitKapitalpositionen) && <h2 className="abschnitt">{t('prozess.einbezogen')}</h2>}

                {/*
                  **Ein abgeschalteter Knopf ohne Grund ist eine Sackgasse** —
                  genau die, die hier gerade abgeschafft wird (R825). Ohne
                  Konzernabschluß gibt es keine Bewertungsebene und keine
                  Periode, und ohne die keine Gliederung.
                */}
                {bezug === null && darfAbgrenzen && (
                  <p className="erlaeuterung">{t('schritte.abgrenzung.ohnebezug')}</p>
                )}

                {/*
                  **Beteiligungen und Eigenkapital wählt die Kapitalkonsolidierung**
                  (7.4 Abschnitt 34.12, R1603) — zwei Listen, jede mit ihrem Satz.
                */}
                {mitKapitalpositionen && (
                  <>
                    <Positionsmarken
                      titel={t('schritte.kapko.beteiligungen')}
                      leer={t('schritte.kapko.beteiligungen.leer')}
                      hinweis={t('schritte.kapko.beteiligungen.hinweis')}
                      positionen={abgrenzung.beteiligungen}
                      name={(p) => positionswahl.find((e) => e.wert === p)?.text ?? p}
                      darf={darfAbgrenzen}
                      laeuft={laeuft}
                      ohneBezug={bezug === null}
                      aufEntfernen={(p) => setAbgrenzung((a) => ({ ...a, beteiligungen: a.beteiligungen.filter((x) => x !== p) }))}
                      aufWaehlen={() => setWaehlt('beteiligungen')}
                    />
                    <Positionsmarken
                      titel={t('schritte.kapko.eigenkapital')}
                      leer={t('schritte.kapko.eigenkapital.leer')}
                      hinweis={t('schritte.kapko.eigenkapital.hinweis')}
                      positionen={abgrenzung.eigenkapital}
                      name={(p) => positionswahl.find((e) => e.wert === p)?.text ?? p}
                      darf={darfAbgrenzen}
                      laeuft={laeuft}
                      ohneBezug={bezug === null}
                      aufEntfernen={(p) => setAbgrenzung((a) => ({ ...a, eigenkapital: a.eigenkapital.filter((x) => x !== p) }))}
                      aufWaehlen={() => setWaehlt('eigenkapital')}
                    />
                  </>
                )}

                {waehltPositionen && (
                  <label className="feld abgrenzungsfeld">
                    <span className="name">{t(mitMarge ? 'schritte.marge.positionen' : mitErtragspositionen ? 'schritte.ertrag.positionen' : 'schritte.abgrenzung.positionen')}</span>

                    {/*
                      **Marken nebeneinander statt einer Liste untereinander**
                      (Entscheidung vom 2026-09-27): Zwei Positionen brauchen
                      eine Zeile statt vier. Gekürzt wird der Name nicht — sonst
                      wäre wieder zu raten, welche Position gemeint ist.
                    */}
                    {abgrenzung.positionen.length === 0 ? (
                      // Der Stand steht im Feld, nicht als Erläuterung daneben (R1630).
                      <p className="gedaempft">{t(mitMarge ? 'schritte.marge.positionen.leer' : mitErtragspositionen ? 'schritte.ertrag.positionen.leer' : 'schritte.abgrenzung.leer')}</p>
                    ) : (
                      <ul className="markenreihe">
                        {abgrenzung.positionen.map((p) => (
                          <li key={p}>
                            <span>{positionswahl.find((e) => e.wert === p)?.text ?? p}</span>
                            {darfAbgrenzen && (
                              <button
                                type="button"
                                className="markenkreuz"
                                disabled={laeuft}
                                aria-label={t('schritte.abgrenzung.entfernen')}
                                title={t('schritte.abgrenzung.entfernen')}
                                onClick={() =>
                                  setAbgrenzung((a) => ({
                                    ...a,
                                    positionen: a.positionen.filter((x) => x !== p),
                                  }))
                                }
                              >
                                ×
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {darfAbgrenzen && (
                      <button
                        type="button"
                        disabled={laeuft || bezug === null}
                        onClick={() => setWaehlt('positionen')}
                      >
                        {t('schritte.abgrenzung.positionen.waehlen')}
                      </button>
                    )}

                    <span className="erlaeuterung">
                      {t(mitMarge ? 'schritte.marge.positionen.hinweis' : mitErtragspositionen ? 'schritte.ertrag.positionen.hinweis' : 'schritte.abgrenzung.positionen.hinweis')}
                    </span>
                  </label>
                )}

                {fuehrtKonto && (
                <label className="feld abgrenzungsfeld">
                  <span className="name">{t('schritte.abgrenzung.differenzkonto')}</span>
                  {/*
                    **Das Differenzenkonto wird im Feld gewählt** (R1043): Das
                    Fenster dafür ging am 2026-10-04 im Kontofeld auf. „Konto
                    aus dem Regelwerk" ist der leere Eintrag des Feldes.
                  */}
                  {darfAbgrenzen ? (
                    <Kontofeld
                      wert={abgrenzung.differenzkonto}
                      eintraege={kontowahl}
                      leer={t('schritte.abgrenzung.differenzkonto.keins')}
                      beschriftung={t('schritte.abgrenzung.differenzkonto')}
                      ledger={bezug?.ledger}
                      periode={bezug?.periode}
                      abgeschaltet={laeuft}
                      aufWahl={(konto) => setAbgrenzung((a) => ({ ...a, differenzkonto: konto }))}
                    />
                  ) : (
                    <span>
                      {abgrenzung.differenzkonto === ''
                        ? t('schritte.abgrenzung.differenzkonto.keins')
                        : kontobezeichnung(abgrenzung.differenzkonto)}
                    </span>
                  )}
                  <span className="erlaeuterung">
                    {t('schritte.abgrenzung.differenzkonto.hinweis')}
                  </span>
                </label>
                )}

                {/*
                  **Das Differenzenkonto für Währungsdifferenzen** (2.9
                  Abschnitt 13.8, R1482): Pflicht wie das Differenzenkonto,
                  darf dasselbe sein.
                */}
                {trenntWaehrung && (
                  <label className="feld abgrenzungsfeld">
                    <span className="name">{t('schritte.abgrenzung.differenzkontoWaehrung')}</span>
                    {darfAbgrenzen ? (
                      <Kontofeld
                        wert={abgrenzung.differenzkontoWaehrung}
                        eintraege={kontowahl}
                        leer={t('schritte.abgrenzung.differenzkontoWaehrung.keins')}
                        beschriftung={t('schritte.abgrenzung.differenzkontoWaehrung')}
                        ledger={bezug?.ledger}
                        periode={bezug?.periode}
                        abgeschaltet={laeuft}
                        aufWahl={(konto) => setAbgrenzung((a) => ({ ...a, differenzkontoWaehrung: konto }))}
                      />
                    ) : (
                      <span>
                        {abgrenzung.differenzkontoWaehrung === ''
                          ? t('schritte.abgrenzung.differenzkontoWaehrung.keins')
                          : kontobezeichnung(abgrenzung.differenzkontoWaehrung)}
                      </span>
                    )}
                    <span className="erlaeuterung">
                      {t('schritte.abgrenzung.differenzkontoWaehrung.hinweis')}
                    </span>
                  </label>
                )}

                {/*
                  **Die Konzernmarge** (2.9 Abschnitt 13.16, R1561, R1562):
                  eine Standardmarge und je Lieferbeziehung eine eigene. **Die
                  Definition steht bei der Eingabe** — Auflage des
                  Auftraggebers vom 2026-10-11: „Diese Definition muss
                  eindeutig bei der Eingabe der Margen im Konsolidierungsprozess
                  angezeigt werden."
                */}
                {mitMarge && (
                  <>
                    <h2 className="abschnitt">{t('schritte.marge')}</h2>
                    <p className="erlaeuterung margendefinition">{t('schritte.marge.definition')}</p>

                    <label className="feld abgrenzungsfeld">
                      <span className="name">{t('schritte.marge.standard')}</span>
                      {darfAbgrenzen ? (
                        <span className="prozentfeld">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={abgrenzung.standardmarge}
                            disabled={laeuft}
                            aria-label={t('schritte.marge.standard')}
                            onChange={(e) => setAbgrenzung((a) => ({ ...a, standardmarge: e.target.value }))}
                          />
                          <span>%</span>
                        </span>
                      ) : (
                        <span>
                          {abgrenzung.standardmarge === ''
                            ? t('schritte.marge.keine')
                            : `${abgrenzung.standardmarge} %`}
                        </span>
                      )}
                      <span className="erlaeuterung">{t('schritte.marge.standard.hinweis')}</span>
                    </label>

                    <div className="feld abgrenzungsfeld">
                      <span className="name">{t('schritte.marge.paare')}</span>
                      {abgrenzung.paarmargen.length === 0 ? (
                        <p className="gedaempft">{t('schritte.marge.paare.leer')}</p>
                      ) : (
                        <ul className="paarmargen">
                          {abgrenzung.paarmargen.map((p, i) => (
                            <li key={i}>
                              {darfAbgrenzen ? (
                                <>
                                  <Auswahlfeld
                                    wert={p.lieferant}
                                    eintraege={gesellschaftswahl}
                                    beschriftung={t('schritte.marge.lieferant')}
                                    leer={t('schritte.marge.lieferant')}
                                    abgeschaltet={laeuft}
                                    aufWahl={(w) => aenderePaar(i, { lieferant: w })}
                                  />
                                  <span aria-hidden="true">→</span>
                                  <Auswahlfeld
                                    wert={p.empfaenger}
                                    eintraege={gesellschaftswahl}
                                    beschriftung={t('schritte.marge.empfaenger')}
                                    leer={t('schritte.marge.empfaenger')}
                                    abgeschaltet={laeuft}
                                    aufWahl={(w) => aenderePaar(i, { empfaenger: w })}
                                  />
                                  <span className="prozentfeld">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={p.marge}
                                      disabled={laeuft}
                                      aria-label={t('schritte.marge.marge')}
                                      onChange={(e) => aenderePaar(i, { marge: e.target.value })}
                                    />
                                    <span>%</span>
                                  </span>
                                  <button
                                    type="button"
                                    className="markenkreuz"
                                    disabled={laeuft}
                                    aria-label={t('schritte.marge.paar.entfernen')}
                                    title={t('schritte.marge.paar.entfernen')}
                                    onClick={() =>
                                      setAbgrenzung((a) => ({
                                        ...a,
                                        paarmargen: a.paarmargen.filter((_, j) => j !== i),
                                      }))
                                    }
                                  >
                                    ×
                                  </button>
                                </>
                              ) : (
                                <span>
                                  {gesellschaftsname(p.lieferant)} → {gesellschaftsname(p.empfaenger)}: {p.marge} %
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {darfAbgrenzen && (
                        <button
                          type="button"
                          disabled={laeuft}
                          onClick={() =>
                            setAbgrenzung((a) => ({
                              ...a,
                              paarmargen: [...a.paarmargen, { lieferant: '', empfaenger: '', marge: '' }],
                            }))
                          }
                        >
                          {t('schritte.marge.paar.neu')}
                        </button>
                      )}
                      <span className="erlaeuterung">{t('schritte.marge.paare.hinweis')}</span>
                    </div>
                  </>
                )}

              </>
            )}

            {handbuchwerte.length > 0 && (
              <>
                {/* Die Überschrift des Blattes (7.2 Abschnitt 53, R1630). */}
                <h2 className="abschnitt">{t('prozess.bucht')}</h2>

                {/*
                  **Der Handbuchwert steht neben der Abweichung und nicht
                  hinter ihr** (R817): Wer übersteuert, soll sehen, was er
                  ersetzt — sonst wäre das Konzernhandbuch eine Auskunft, die
                  nicht mehr stimmt.
                */}
                <table ref={spaltenrahmen1} className="handbuchwerte ziehbaretabelle">
                  <Spaltenmassleiste masse={spaltenmasse1} />
                  <thead>
                    <tr>
                      <th>{t('prozess.angabe')}</th>
                      <th>{t('prozess.ausHandbuch')}</th>
                      {/* Ohne Recht und ohne Abweichung bliebe die Spalte leer (R1180). */}
                      {(darfUebersteuern || weichtAb) && <th>{t('prozess.abweichend')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {handbuchwerte.map((name) => (
                      <tr key={name}>
                        <td>
                          {t(feldname(name) as Sprachschluessel)}
                        </td>
                        <td className="gedaempft">{ausHandbuch(name, parameter)}</td>
                        {(darfUebersteuern || weichtAb) && (
                        <td>
                          {!darfUebersteuern ? (
                            // Wie im Konzernhandbuch geschrieben (R1633).
                            <Handbuchwert name={name} wert={abweichung[name]} />
                          ) : istKonto(name) ? (
                            <Kontofeld
                              wert={abweichung[name] ?? ''}
                              eintraege={kontowahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.abweichend.leer')}
                              beschriftung={t(feldname(name) as Sprachschluessel)}
                              abgeschaltet={!weichtAb || laeuft}
                              aufWahl={(wert) =>
                                setAbweichung((a) => ({ ...a, [name]: wert }))
                              }
                            />
                          ) : handbuchwertart(name) === 'betrag' ? (
                            // **Dasselbe Betragsfeld wie im Konzernhandbuch** (R1634):
                            // Es nimmt 10000 wie 10.000,00 und zeigt nach dem
                            // Verlassen 10.000,00.
                            <Zahlenfeld
                              klasse="nurfeld"
                              darstellung="betrag"
                              beschriftung={t(feldname(name) as Sprachschluessel)}
                              wert={abweichung[name] || null}
                              gesperrt={!weichtAb || laeuft}
                              aufWert={(zahl) => setAbweichung((a) => ({ ...a, [name]: zahl ?? '' }))}
                            />
                          ) : (
                            // Eine Anzahl wie im Konzernhandbuch (R1634).
                            <input
                              type="number"
                              min={0}
                              aria-label={t(feldname(name) as Sprachschluessel)}
                              value={abweichung[name] ?? ''}
                              disabled={!weichtAb || laeuft}
                              placeholder={t('prozess.abweichend.leer')}
                              onChange={(e) =>
                                setAbweichung((a) => ({ ...a, [name]: e.target.value }))
                              }
                            />
                          )}
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/*
                  **Abgewichen wird bewußt, nicht durch Ausfüllen** (R817,
                  Entscheidung vom 2026-09-27): *„Abweichungen sind nicht per
                  se schlimm. Sie müssen nur klar, transparent dargestellt sein
                  und bewußt angesteuert werden."*
                */}
                {darfUebersteuern && (
                  <label className="abweichungsschalter">
                    <input
                      type="checkbox"
                      checked={weichtAb}
                      disabled={laeuft}
                      onChange={(e) => setWeichtAb(e.target.checked)}
                    />
                    <span>{t('prozess.abweichend.schalter')}</span>
                  </label>
                )}
                {handbuchabruf?.ausgefallen ? (
                  <Abrufausfall abruf={handbuchabruf} />
                ) : (
                  <p className="erlaeuterung">
                    {ausgabe === null
                      ? t('prozess.ausHandbuch.ohne')
                      : t('prozess.ausHandbuch.ausgabe', { nummer: String(ausgabe) })}
                  </p>
                )}
              </>
            )}

            {/*
              **Ein Prozeß zeigt, woran er rechnet** (R822) — bei der
              Kapitalkonsolidierung ihre Beteiligungen.

              **Und das Blatt muß achtzig Gesellschaften tragen**, Auflage des
              Auftraggebers: Oben steht die Zählung, darunter zuerst, was eine
              Frage aufwirft. Der Rest kommt auf einen Klick, und gesucht wird
              nach Gesellschaft — achtzig Zeilen, von denen achtundsiebzig in
              Ordnung sind, beantworten nichts.
            */}
            {grundlage !== null && grundlage.gesamt > 0 && (
              <>
                <h2 className="abschnitt">{t('prozess.grundlage.titel')}</h2>

                {/*
                  **Die Zählung steht nur, wo sie etwas sagt** (Entscheidung
                  vom 2026-09-27): Bei einer Beteiligung wiederholt sie die
                  Zeile darunter. Erst wo die Liste einblendet und ausblendet,
                  gibt sie jeder Zahl ihr Maß (R809).
                */}
                {!kurz && (
                <p className="zaehlung">
                  {/* Eine Beteiligung ist keine "1 Beteiligungen". */}
                  {grundlage.gesamt === 1
                    ? t('prozess.grundlage.zaehlung.eine', {
                        voll: anzahlwort(grundlage.vollEinbezogen),
                        erfasst: anzahlwort(grundlage.mitErfassung),
                      })
                    : t('prozess.grundlage.zaehlung', {
                        gesamt: anzahlwort(grundlage.gesamt),
                        voll: anzahlwort(grundlage.vollEinbezogen),
                        erfasst: anzahlwort(grundlage.mitErfassung),
                      })}
                </p>
                )}

                <div className="reihe">
                  <input
                    type="search"
                    value={suche}
                    placeholder={t('prozess.grundlage.suche')}
                    aria-label={t('prozess.grundlage.suche')}
                    onChange={(e) => setSuche(e.target.value)}
                  />
                  {!alleZeigen && versteckt > 0 && (
                    <button type="button" onClick={() => setAlleZeigen(true)}>
                      {t('prozess.grundlage.alle', { anzahl: anzahlwort(versteckt) })}
                    </button>
                  )}
                </div>

                {sichtbare.length === 0 ? (
                  <p className="erlaeuterung">{t('prozess.grundlage.nichts')}</p>
                ) : (
                  <table ref={spaltenrahmen2} className="beteiligungen ziehbaretabelle">
                    <Spaltenmassleiste masse={spaltenmasse2} />
                    <thead>
                      <tr>
                        {/*
                          **Der Kopf sortiert** (Auftrag vom 2026-09-27): Bei
                          achtzig Gesellschaften ist die Reihenfolge die halbe
                          Suche. Ein zweiter Klick dreht sie um.
                        */}
                        <th>
                          <button
                            type="button"
                            className="sortierkopf"
                            onClick={() => sortiereNach('tochter')}
                          >
                            {t('prozess.grundlage.tochter')}
                            {sortierung === 'tochter' && (
                              <span className="pfeil">{absteigend ? '▾' : '▴'}</span>
                            )}
                          </button>
                        </th>
                        <th className="zahl">
                          <button
                            type="button"
                            className="sortierkopf"
                            onClick={() => sortiereNach('quote')}
                          >
                            {t('prozess.grundlage.quote')}
                            {sortierung === 'quote' && (
                              <span className="pfeil">{absteigend ? '▾' : '▴'}</span>
                            )}
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className="sortierkopf"
                            onClick={() => sortiereNach('erwerb')}
                          >
                            {t('prozess.grundlage.erwerb')}
                            {sortierung === 'erwerb' && (
                              <span className="pfeil">{absteigend ? '▾' : '▴'}</span>
                            )}
                          </button>
                        </th>
                        <th>{t('prozess.grundlage.quelle')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sichtbare.map((b) => (
                        <tr key={b.schluessel} className={b.auffaellig ? 'auffaellig' : undefined}>
                          <td>
                            {gesellschaftsname(b.tochter)}
                            <span className="klein gedaempft">
                              {' · '}
                              {t('prozess.grundlage.von', { mutter: gesellschaftsname(b.mutter) })}
                            </span>
                          </td>
                          {/* Derselbe Anteil in derselben Form wie im Register: „80 %" (R840). */}
                          <td className="zahl">{anteil(b.quote)}</td>
                          <td>
                            {b.erwerbsperiode === null
                              ? t('prozess.grundlage.ohneerwerb')
                              : periodenname(b.erwerbsperiode)}
                          </td>
                          <td className="gedaempft">
                            {b.einbeziehungsart === 'voll'
                              ? t(`prozess.grundlage.quelle.${b.quelle}` as Sprachschluessel)
                              : t('prozess.grundlage.nichtvoll', {
                                  art: t(`einbeziehungsart.${b.einbeziehungsart}` as Sprachschluessel),
                                })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <p className="erlaeuterung">{t('prozess.grundlage.hinweis')}</p>
              </>
            )}

            {/*
              **Der konzerneinheitliche Ausweis** (R818–R820), seit dem
              2026-10-12 in zwei Schritten (2.9 Abschnitt 15.9, R1615): Die
              Ausweiskorrektur gliedert eine Position oder ein Konto um — auf
              den eigenen Konten entlastet oder in einer Summe auf einem Konto
              der Position (R1616, R1617). Die Saldierung behält ihre Zeile mit
              zwei Positionen und je einem Sammelkonto.
            */}
            {istKorrekturprozess && (
              <>
                <h2 className="abschnitt">
                  {t(istSaldierung ? 'prozess.saldierung.titel' : 'prozess.korrektur.titel')}
                </h2>

                {korrekturen.length === 0 ? (
                  <p className="erlaeuterung">
                    {t(istSaldierung ? 'prozess.saldierung.leer' : 'prozess.korrektur.leer')}
                  </p>
                ) : (
                  <table ref={spaltenrahmen3} className="korrekturen ziehbaretabelle">
                    <Spaltenmassleiste masse={spaltenmasse3} />
                    <thead>
                      {istSaldierung ? (
                        <tr>
                          <th>{t('prozess.saldierung.seite')}</th>
                          <th>{t('prozess.saldierung.sammelkonto')}</th>
                          {darfKorrigieren && <th />}
                        </tr>
                      ) : (
                        <tr>
                          <th>{t('prozess.korrektur.quelle')}</th>
                          <th>{t('prozess.korrektur.entlastung')}</th>
                          <th>{t('prozess.korrektur.ziel')}</th>
                          {darfKorrigieren && <th />}
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {korrekturen.map((regel, i) => !darfKorrigieren ? (
                        // Ohne Recht steht die Korrektur als Text da (R1180).
                        istSaldierung ? (
                          <tr key={i}>
                            <td>
                              {positionsname(regel.quelle)}
                              {regel.gegenquelle && <div>{positionsname(regel.gegenquelle)}</div>}
                            </td>
                            <td>
                              {kontobezeichnung(regel.ziel)}
                              {regel.gegenziel && <div>{kontobezeichnung(regel.gegenziel)}</div>}
                            </td>
                          </tr>
                        ) : (
                          <tr key={i}>
                            <td>{istPosition(regel.quelle) ? positionsname(regel.quelle) : kontobezeichnung(regel.quelle)}</td>
                            <td>
                              {istPosition(regel.quelle)
                                ? regel.entlastung
                                  ? kontobezeichnung(regel.entlastung)
                                  : t('prozess.korrektur.eigene')
                                : t('prozess.korrektur.aufquelle')}
                            </td>
                            <td>{kontobezeichnung(regel.ziel)}</td>
                          </tr>
                        )
                      ) : istSaldierung ? (
                        <tr key={i}>
                          <td>
                            <Kontofeld
                              waehlt="position"
                              wert={regel.quelle}
                              eintraege={positionswahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.saldierung.quelle')}
                              beschriftung={t('prozess.saldierung.quelle')}
                              abgeschaltet={laeuft}
                              aufWahl={(quelle) => aendere(i, { quelle })}
                            />
                            <Kontofeld
                              waehlt="position"
                              wert={regel.gegenquelle ?? ''}
                              eintraege={positionswahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.korrektur.gegenquelle')}
                              beschriftung={t('prozess.korrektur.gegenquelle')}
                              abgeschaltet={laeuft}
                              aufWahl={(gegenquelle) => aendere(i, { gegenquelle })}
                            />
                          </td>
                          <td>
                            <Kontofeld
                              wert={regel.ziel}
                              eintraege={kontowahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.saldierung.ziel')}
                              beschriftung={t('prozess.saldierung.ziel')}
                              abgeschaltet={laeuft}
                              aufWahl={(ziel) => aendere(i, { ziel })}
                            />
                            <Kontofeld
                              wert={regel.gegenziel ?? ''}
                              eintraege={kontowahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.korrektur.gegenziel')}
                              beschriftung={t('prozess.korrektur.gegenziel')}
                              abgeschaltet={laeuft}
                              aufWahl={(gegenziel) => aendere(i, { gegenziel })}
                            />
                          </td>
                          <td className="handgriff">
                            <button
                              type="button"
                              className="schmal gefahr"
                              disabled={laeuft}
                              onClick={() =>
                                setKorrekturen((bisher) => bisher.filter((_, x) => x !== i))
                              }
                            >
                              {t('schritte.abgrenzung.entfernen')}
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={i}>
                          <td>
                            {/* Eine Position oder ein Konto, aus demselben Baum (R1616). */}
                            <Kontofeld
                              waehlt="beides"
                              wert={regel.quelle}
                              eintraege={quellwahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.korrektur.quelle')}
                              beschriftung={t('prozess.korrektur.quelle')}
                              abgeschaltet={laeuft}
                              aufWahl={(quelle) =>
                                // Eine andere Quelle nimmt die Entlastung zurück:
                                // Sie muss unter der Quelle liegen (R1617).
                                aendere(i, { quelle, entlastung: null })
                              }
                            />
                          </td>
                          <td>
                            {istPosition(regel.quelle) ? (
                              <Kontofeld
                                wert={regel.entlastung ?? ''}
                                eintraege={kontowahl}
                                unter={regel.quelle}
                                ledger={bezug?.ledger}
                                periode={bezug?.periode}
                                leer={t('prozess.korrektur.eigene')}
                                beschriftung={t('prozess.korrektur.entlastung')}
                                abgeschaltet={laeuft}
                                aufWahl={(entlastung) => aendere(i, { entlastung: entlastung || null })}
                              />
                            ) : (
                              <span className="erlaeuterung">
                                {regel.quelle === '' ? '' : t('prozess.korrektur.aufquelle')}
                              </span>
                            )}
                          </td>
                          <td>
                            <Kontofeld
                              wert={regel.ziel}
                              eintraege={kontowahl}
                              ledger={bezug?.ledger}
                              periode={bezug?.periode}
                              leer={t('prozess.korrektur.ziel')}
                              beschriftung={t('prozess.korrektur.ziel')}
                              abgeschaltet={laeuft}
                              aufWahl={(ziel) => aendere(i, { ziel })}
                            />
                          </td>
                          <td className="handgriff">
                            <button
                              type="button"
                              className="schmal gefahr"
                              disabled={laeuft}
                              onClick={() =>
                                setKorrekturen((bisher) => bisher.filter((_, x) => x !== i))
                              }
                            >
                              {t('schritte.abgrenzung.entfernen')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {darfKorrigieren && (
                  <div className="knopfreihe">
                    <button
                      type="button"
                      disabled={laeuft}
                      onClick={() =>
                        setKorrekturen((bisher) => [
                          ...bisher,
                          {
                            sorte: istSaldierung ? 'saldieren' : 'umgliedern',
                            quelle: '',
                            ziel: '',
                            gegenquelle: null,
                            gegenziel: null,
                            entlastung: null,
                          },
                        ])
                      }
                    >
                      {t(istSaldierung ? 'prozess.saldierung.neu' : 'prozess.korrektur.neu')}
                    </button>
                  </div>
                )}

                <p className="erlaeuterung">
                  {t(istSaldierung ? 'prozess.saldierung.hinweis' : 'prozess.korrektur.hinweis')}
                </p>
                {/* Beide rechnen am Ende, untereinander in ihrer Folge (R1618). */}
                <p className="erlaeuterung">{t('prozess.amende.vermerk')}</p>
              </>
            )}

            {/*
              **Die Abgrenzung nur, wo der Rechenkern sie liest** (R800): Ein
              Feld an einem Schritt, der die Angabe übergeht, wäre ein
              Versprechen ins Leere.

              **Und sie steht hier, nicht im Vorlagenbereich** (R825). Bis zum
              2026-09-27 stand an dieser Stelle ein Satz, der woandershin
              verwies — dabei gehört die Abgrenzung nach R789 dem
              Konzernabschluß und darf von Jahr zu Jahr abweichen.
            */}
          </div>
        )}

        {blatt === 'belege' && (
          <div className="belegblatt">
            {rechnetNiemand ? (
              <p className="leerhinweis">{t('prozess.ohnevorgang')}</p>
            ) : (
              <>
                {/*
                  **Ein Monat zur Zeit** (R815): Bestände werden je Periode
                  gemessen, und ein Ruf über zwölf Monate wächst bei achtzig
                  Gesellschaften mit der Zahl der Paare, die miteinander
                  Geschäft machen.

                  **Ohne Zahl am Monat** (Entscheidung vom 2026-09-27): Sie
                  beantwortete keine Frage -- wie viele Belege der gewählte
                  Monat trägt, steht darunter, und für die elf anderen ist es
                  keine Auskunft, sondern Rauschen.
                */}
                <div className="monatsleiste">
                  {(nachweis?.monate ?? []).map((m) => (
                    <button
                      type="button"
                      key={m.periode}
                      className={m.periode === nachweis?.periode ? 'monat aktiv' : 'monat'}
                      aria-pressed={m.periode === nachweis?.periode}
                      disabled={laeuft}
                      onClick={() => setMonat(m.periode)}
                    >
                      {periodenkuerzel(m.periode)}
                    </button>
                  ))}
                </div>

                {/*
                  **Auch ein Monat kann achtzig Gesellschaften tragen**
                  (Auftrag vom 2026-09-27): „Es gibt keinen Filter, damit ich
                  zielgenau suchen kann, wenn wirklich mal 80 Firmen da drin
                  stehen." Gesucht wird nach Gesellschaft, geordnet nach dem,
                  wonach jemand sucht — dem Namen, der Art des Falls oder dem
                  Betrag, wenn er den größten sucht.
                */}
                {(nachweis?.faelle.length ?? 0) > 1 && (
                  <div className="reihe">
                    <input
                      type="search"
                      value={fallsuche}
                      placeholder={t('prozess.belege.suche')}
                      aria-label={t('prozess.belege.suche')}
                      onChange={(e) => setFallsuche(e.target.value)}
                    />
                    <label className="ordnungswahl">
                      <span>{t('prozess.belege.ordnung')}</span>
                      <select
                        value={fallordnung}
                        onChange={(e) =>
                          setFallordnung(e.target.value as 'gesellschaft' | 'regel' | 'betrag')
                        }
                      >
                        <option value="gesellschaft">{t('prozess.belege.nachgesellschaft')}</option>
                        <option value="regel">{t('prozess.belege.nachregel')}</option>
                        <option value="betrag">{t('prozess.belege.nachbetrag')}</option>
                      </select>
                    </label>
                  </div>
                )}

                {/*
                  **Ein Monat von vor den Belegen** (2.13 Abschnitt 21.6): Er
                  trägt keine, und das Blatt sagt es, statt die alte
                  Gruppierung weiterzuführen.
                */}
                {nachweis?.ohneBelege && (
                  <div className="leerhinweis">{t('prozess.belege.ohne')}</div>
                )}

                {nachweis && !nachweis.ohneBelege && nachweis.faelle.length === 0 && (
                  <div className="leerhinweis">
                    {nachweis.periode === null
                      ? t('schritte.nachweis.nielief')
                      : t('prozess.belege.keine', { monat: periodenname(nachweis.periode) })}
                  </div>
                )}

                {/*
                  **Die Meldung des Monats steht nur, wo es keine Belege gibt**
                  (R791, Entscheidung vom 2026-09-27): Dann ist sie die
                  eigentliche Auskunft -- „kein Beteiligungsbuchwert für
                  Holding AG an Tochter 1 GmbH" ist genau das, was jemand
                  sucht, der fragt, warum kein Firmenwert entstanden ist.

                  **Neben den Fällen ist sie eine Wiederholung:** Sie nennt
                  dieselben Gesellschaften und dieselben Beträge, die eine
                  Zeile tiefer stehen -- und die stehen dort mit ihrem Konto.
                */}
                {(nachweis?.faelle.length ?? 0) === 0 && nachweis?.monate
                  .filter((m) => m.periode === nachweis.periode && m.meldungen.length > 0)
                  .map((m) => (
                    <p key={m.periode} className="erlaeuterung monatsmeldung">
                      {m.meldungen.map((x) => satz(x)).join(' · ')}
                    </p>
                  ))}

                {faelle.map((fall) => (
                  <Fallzeile
                    key={fall.schluessel}
                    fall={fall}
                    offen={offen === fall.schluessel}
                    aufKlappen={() =>
                      setOffen((bisher) => (bisher === fall.schluessel ? null : fall.schluessel))
                    }
                  />
                ))}
              </>
            )}

            {meldung && <p className="meldung">{meldung}</p>}
          </div>
        )}
      </div>

      {/*
        **Das Auswahlfenster nennt in seinem Kopf, woran es hängt** (R826) —
        den Prozeß und das Feld. Ein Fenster über einem Fenster verdeckt das
        untere, und am 2026-09-26 stand die Abgrenzung schon einmal unter dem
        falschen Schritt.
      */}
      {waehlt !== null && bezug !== null && (
        <Gliederungswahl
          herkunft={schritt.namen.de || werkzeugname}
          ledger={bezug.ledger}
          periode={bezug.periode}
          gewaehlt={abgrenzung[waehlt]}
          aufUebernehmen={(gewaehlt) => {
            setAbgrenzung((a) => ({ ...a, [waehlt]: gewaehlt }))
            setWaehlt(null)
          }}
          aufSchliessen={() => setWaehlt(null)}
        />
      )}
    </Dialogfenster>
  )
}

/**
 * Wonach ein Fall in der Liste steht — die Gesellschaften, sonst die Regel.
 *
 * **Dasselbe, was zugeklappt dasteht**: Wer nach dem sortiert, was er liest,
 * findet die Zeile dort, wo er sie sucht.
 */
function namenswahl(fall: Belegfall): string {
  return fall.gesellschaften.length === 0
    ? regelname(fall.regel)
    : fall.gesellschaften.map((g) => gesellschaftsname(g)).join(' + ')
}

/**
 * Ein Beleg, zugeklappt mit seiner Nummer (R815, R1635, R1640).
 *
 * **Zugeklappt steht, was die Frage beantwortet**, ob man aufklappen muß: wer
 * beteiligt ist und welcher Beleg es ist.
 */
/** Ob Beträge als Soll und Haben stehen — eingestellt in der Fußleiste. */
const sollHabenAnsicht = () => aktuelleVorzeichenart() === 'sollhaben'

/** Ob die Zeile die Spiegelung des laufenden Ergebnisses ist (R1639). */
const istAusweis = (z: Buchungszeile) => z.regel === 'ergebnisspiegelung' && z.bewegungsart === 'BWA-ERG'

function Fallzeile({
  fall,
  offen,
  aufKlappen,
}: {
  fall: Belegfall
  offen: boolean
  aufKlappen: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('prozessfenster:fallzeile')

  return (
    <div className={offen ? 'belegfall offen' : 'belegfall'}>
      <button type="button" className="fallkopf" onClick={aufKlappen}>
        <span className="pfeil">{offen ? '▾' : '▸'}</span>
        {fall.nummer && <span className="belegnummer">{fall.nummer}</span>}
        {/*
          **Der Name, nicht der Schluessel** (R778, R792):
          'aufwandertragkonsolidierung' ist keine Auskunft -- und der
          Namensgeber dafuer steht seit jeher bereit.

          **Und die Regel steht nur da, wo daneben Gesellschaften stehen.**
          Wo keine stehen, ist sie selbst der Name des Falls; zweimal
          dasselbe und einmal "ohne Partnergesellschaft" waeren zwei Angaben,
          von denen eine nichts beantwortet.
        */}
        <span className="beteiligte">
          {fall.gesellschaften.length === 0
            ? regelname(fall.regel)
            : fall.gesellschaften.map((g) => gesellschaftsname(g)).join(' + ')}
        </span>
        {fall.gesellschaften.length > 0 && (
          <span className="regel gedaempft">{regelname(fall.regel)}</span>
        )}
        {/*
          **Die Zeilenzahl steht nicht da** (Entscheidung vom 2026-09-27): Sie
          beantwortet dieselbe Frage wie die Tabelle darunter, und die sieht
          man beim Aufklappen.

          **Und keine Probe:** Ein Beleg, der nicht aufgeht, wird nicht
          gespeichert (2.13 Abschnitt 21, R1638). Bis zum 2026-10-12 stand
          hier ein nackter Restbetrag. Die eine benannte Ausnahme sagt es in
          Worten (R1641).
        */}
        {fall.ausnahme && <span className="ausnahme">{t('prozess.beleg.ausnahme')}</span>}
      </button>

      {offen && (
        <table ref={spaltenrahmen} className="fallzeilen ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('schritte.nachweis.gesellschaft')}</th>
              <th>{t('schritte.nachweis.konto')}</th>
              {/*
                **Soll und Haben statt Plus und Minus** (Auftrag vom
                2026-09-27): „Wir arbeiten mit Buchhaltern." Eingestellt wird
                das einmal in der Fußleiste und gilt dann überall.
              */}
              {sollHabenAnsicht() ? (
                <>
                  <th className="zahl">{t('beleg.spalte.soll')}</th>
                  <th className="zahl">{t('beleg.spalte.haben')}</th>
                </>
              ) : (
                <th className="zahl">{t('schritte.nachweis.betrag')}</th>
              )}
              <th>{t('schritte.nachweis.erlaeuterung')}</th>
            </tr>
          </thead>
          <tbody>
            {/*
              **Die Spiegelung des Ergebnisses steht grau unter den Zeilen**
              (R1639): Sie ist Ausweis und keine Buchung und zählt nicht zur
              Summe des Belegs.
            */}
            {[...fall.zeilen.filter((z) => !istAusweis(z)), ...fall.zeilen.filter(istAusweis)].map((z) => (
              <tr key={z.id} className={istAusweis(z) ? 'ausweis' : undefined}>
                <td className="gedaempft">{z.einheit ? gesellschaftsname(z.einheit) : ''}</td>
                <td>{kontobezeichnung(z.konto)}</td>
                {sollHabenAnsicht() ? (
                  <>
                    <td className="zahl">{sollHaben(z.betrag).soll}</td>
                    <td className="zahl">{sollHaben(z.betrag).haben}</td>
                  </>
                ) : (
                  <td className="zahl"><Betrag wert={z.betrag} /></td>
                )}
                <td className="gedaempft">{istAusweis(z) ? t('prozess.beleg.ausweis') : satz(z.erlaeuterung)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Die Beschriftung eines Handbuchwerts.
 *
 * **Zwei heißen im Handbuch anders**, als sie über die Schnittstelle gehen —
 * die Grenze und die Nutzungsdauer. Der Bildschirm nennt sie so, wie der
 * Anwender sie im Konzernhandbuch kennt.
 */
function feldname(name: string): string {
  if (name === 'icDifferenzGrenze') {
    return 'handbuch.feld.icGrenze'
  }

  if (name === 'goodwillNutzungsdauerMonate') {
    return 'handbuch.feld.nutzungsdauer'
  }

  return `handbuch.feld.${name}`
}

/** Ob die Angabe ein Konto ist — dann wird sie ausgesucht, nicht getippt (R327). */
function istKonto(name: string): boolean {
  return name.endsWith('Konto')
}

/**
 * Was im Konzernhandbuch steht — die Angabe, die die Abweichung ersetzt, und
 * so geschrieben wie dort (7.2 Abschnitt 53, R1633). Bis zum 2026-10-12 stand
 * hier *10000*, im Konzernhandbuch *10.000,00*.
 */
function ausHandbuch(name: string, parameter: Regelparameter | null) {
  if (parameter === null) {
    return ''
  }

  const wert = (parameter as unknown as Record<string, unknown>)[name]

  if (wert === null || wert === undefined || wert === '') {
    return t('prozess.ausHandbuch.leer')
  }

  return <Handbuchwert name={name} wert={wert} />
}

/**
 * Liest die gespeicherten Ausweiskorrekturen — Kaputtes wird zu leer.
 */
/**
 * Liest die gespeicherte Abgrenzung eines Prozesses — Kaputtes wird zu leer.
 *
 * **Ein unlesbares Papier darf das Fenster nicht aufhalten:** Wer es öffnet,
 * will die Abgrenzung setzen, und ein leeres Formular ist dafür der bessere
 * Anfang als eine Fehlermeldung.
 */
/** Eine Marge je Lieferbeziehung, wie das Fenster sie bearbeitet — die Marge in Prozent als Text. */
type Paarmargenentwurf = { lieferant: string; empfaenger: string; marge: string }

/** Welche Liste von Positionen das Auswahlfenster füllt (R1603). */
type Positionsfeld = 'positionen' | 'beteiligungen' | 'eigenkapital'

/** Die Abgrenzung, wie das Fenster sie bearbeitet. */
type Abgrenzungsentwurf = {
  positionen: string[]
  /** Die Beteiligungspositionen der Kapitalkonsolidierung (R1603). */
  beteiligungen: string[]
  /** Die Eigenkapitalpositionen der Kapitalkonsolidierung (R1603). */
  eigenkapital: string[]
  differenzkonto: string
  toleranz: string
  differenzkontoWaehrung: string
  /** Die Standardmarge in Prozent, als Text — leer: keine (R1561). */
  standardmarge: string
  paarmargen: Paarmargenentwurf[]
}

const KEINE_ABGRENZUNG: Abgrenzungsentwurf = {
  positionen: [],
  beteiligungen: [],
  eigenkapital: [],
  differenzkonto: '',
  toleranz: '',
  differenzkontoWaehrung: '',
  standardmarge: '',
  paarmargen: [],
}

/** Ein Anteil als Prozent zum Bearbeiten: 0.2 wird „20". */
function alsProzent(anteil: number | null | undefined): string {
  return anteil === null || anteil === undefined ? '' : String(Math.round(anteil * 1_000_000) / 10_000)
}

/** Ein eingegebenes Prozent als Anteil: „20" und „20,5" werden 0.2 und 0.205; leer bleibt leer. */
function alsAnteil(prozent: string): number | null {
  const text = prozent.trim()
  return text === '' ? null : Number(text.replace(',', '.')) / 100
}

function liesAbgrenzung(json: string | null): Abgrenzungsentwurf {
  if (!json) {
    return KEINE_ABGRENZUNG
  }

  try {
    const inhalt = JSON.parse(json) as {
      positionen?: string[]
      beteiligungen?: string[]
      eigenkapital?: string[]
      differenzkonto?: string | null
      toleranz?: number | null
      differenzkontoWaehrung?: string | null
      standardmarge?: number | null
      paarmargen?: { lieferant: string; empfaenger: string; marge: number }[] | null
    }

    return {
      positionen: Array.isArray(inhalt.positionen) ? inhalt.positionen : [],
      beteiligungen: Array.isArray(inhalt.beteiligungen) ? inhalt.beteiligungen : [],
      eigenkapital: Array.isArray(inhalt.eigenkapital) ? inhalt.eigenkapital : [],
      differenzkonto: inhalt.differenzkonto ?? '',
      toleranz:
        inhalt.toleranz === null || inhalt.toleranz === undefined ? '' : String(inhalt.toleranz),
      differenzkontoWaehrung: inhalt.differenzkontoWaehrung ?? '',
      standardmarge: alsProzent(inhalt.standardmarge),
      paarmargen: Array.isArray(inhalt.paarmargen)
        ? inhalt.paarmargen.map((p) => ({ lieferant: p.lieferant, empfaenger: p.empfaenger, marge: alsProzent(p.marge) }))
        : [],
    }
  } catch {
    return KEINE_ABGRENZUNG
  }
}

/**
 * **Eine gewählte Liste von Positionen**, als Marken nebeneinander, mit
 * Entfernen und Wählen — die Form der Abgrenzung (Entscheidung vom
 * 2026-09-27), für die zwei Listen der Kapitalkonsolidierung (R1603).
 */
function Positionsmarken({
  titel,
  leer,
  hinweis,
  positionen,
  name,
  darf,
  laeuft,
  ohneBezug,
  aufEntfernen,
  aufWaehlen,
}: {
  titel: string
  leer: string
  hinweis: string
  positionen: string[]
  name: (position: string) => string
  darf: boolean
  laeuft: boolean
  ohneBezug: boolean
  aufEntfernen: (position: string) => void
  aufWaehlen: () => void
}) {
  return (
    <label className="feld abgrenzungsfeld">
      <span className="name">{titel}</span>
      {positionen.length === 0 ? (
        <p className="gedaempft">{leer}</p>
      ) : (
        <ul className="markenreihe">
          {positionen.map((p) => (
            <li key={p}>
              <span>{name(p)}</span>
              {darf && (
                <button
                  type="button"
                  className="markenkreuz"
                  disabled={laeuft}
                  aria-label={t('schritte.abgrenzung.entfernen')}
                  title={t('schritte.abgrenzung.entfernen')}
                  onClick={() => aufEntfernen(p)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {darf && (
        <button type="button" disabled={laeuft || ohneBezug} onClick={aufWaehlen}>
          {t('schritte.abgrenzung.positionen.waehlen')}
        </button>
      )}
      <span className="erlaeuterung">{hinweis}</span>
    </label>
  )
}

function liesKorrekturen(json: string | null): Korrekturangabe[] {
  if (!json) {
    return []
  }

  try {
    const inhalt = JSON.parse(json) as Korrekturangabe[]
    return Array.isArray(inhalt) ? inhalt : []
  } catch {
    return []
  }
}

/** Liest die gespeicherte Abweichung — Kaputtes wird zu leer. */
function liesAbweichung(json: string | null): Record<string, string> {
  if (!json) {
    return {}
  }

  try {
    const inhalt = JSON.parse(json) as Record<string, unknown>
    const gelesen: Record<string, string> = {}

    for (const [name, wert] of Object.entries(inhalt)) {
      if (typeof wert === 'string' || typeof wert === 'number') {
        gelesen[name] = String(wert)
      }
    }

    return gelesen
  } catch {
    return {}
  }
}
